import { useState } from 'react';
import type { ProjectComment } from '@canvas/shared-types';

interface CommentsOverlayProps {
  comments: ProjectComment[];
  artboardWidth: number;
  artboardHeight: number;
  zoom: number;
  active: boolean;
  onAdd: (x: number, y: number, body: string) => void;
  onResolve: (id: string) => void;
  onDelete: (id: string) => void;
}

export default function CommentsOverlay({
  comments,
  artboardWidth,
  artboardHeight,
  zoom,
  active,
  onAdd,
  onResolve,
  onDelete,
}: CommentsOverlayProps) {
  const [draft, setDraft] = useState<{ x: number; y: number } | null>(null);
  const [body, setBody] = useState('');
  const [openId, setOpenId] = useState<string | null>(null);

  const scale = zoom / 100;

  return (
    <div
      className={`ps-comments-layer ${active ? 'active' : ''}`}
      style={{
        width: artboardWidth,
        height: artboardHeight,
        transform: `scale(${scale})`,
        transformOrigin: 'top center',
        pointerEvents: active ? 'auto' : 'none',
      }}
      onClick={(e) => {
        if (!active || e.target !== e.currentTarget) return;
        const rect = e.currentTarget.getBoundingClientRect();
        const x = (e.clientX - rect.left) / scale;
        const y = (e.clientY - rect.top) / scale;
        setDraft({ x, y });
        setBody('');
        setOpenId(null);
      }}
    >
      {comments.map((c, idx) => (
        <div
          key={c.commentId}
          className={`ps-comment-pin ${c.resolved ? 'resolved' : ''} ${openId === c.commentId ? 'open' : ''}`}
          style={{ left: c.x, top: c.y }}
        >
          <button
            type="button"
            className="ps-comment-dot"
            title={c.body}
            onClick={(e) => {
              e.stopPropagation();
              setOpenId((id) => (id === c.commentId ? null : c.commentId));
              setDraft(null);
            }}
          >
            {idx + 1}
          </button>
          {openId === c.commentId && (
            <div className="ps-comment-card" onClick={(e) => e.stopPropagation()}>
              <strong>{c.authorName}</strong>
              <p>{c.body}</p>
              <div className="ps-comment-actions">
                <button type="button" onClick={() => onResolve(c.commentId)}>
                  {c.resolved ? 'Reopen' : 'Resolve'}
                </button>
                <button type="button" onClick={() => onDelete(c.commentId)}>
                  Delete
                </button>
              </div>
            </div>
          )}
        </div>
      ))}

      {draft && (
        <div
          className="ps-comment-pin open"
          style={{ left: draft.x, top: draft.y }}
          onClick={(e) => e.stopPropagation()}
        >
          <span className="ps-comment-dot">+</span>
          <div className="ps-comment-card">
            <textarea
              rows={3}
              placeholder="Add a comment…"
              value={body}
              autoFocus
              onChange={(e) => setBody(e.target.value)}
            />
            <div className="ps-comment-actions">
              <button
                type="button"
                onClick={() => {
                  if (!body.trim()) return;
                  onAdd(draft.x, draft.y, body.trim());
                  setDraft(null);
                  setBody('');
                }}
              >
                Post
              </button>
              <button type="button" onClick={() => setDraft(null)}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
